import React, { useState, useEffect } from 'react';
import Button from '../../components/shared/custom-btn';
import { RxCross2 } from 'react-icons/rx';
import DateTimeLocalInput from '../../components/shared/datetime-input';
import CustomTextarea from '../../components/shared/custom-textarea';
import CustomReactSelect from '../../components/shared/custom-react-selector';
import ModalHeading from '../../components/shared/modal-heading';
import GoogleMapComponent from '../../components/shared/google-map';
import CustomInput from '../../components/shared/custom-input';
import { Country, State, City } from 'country-state-city';
import { createJob } from '../../services/jobs-service';

const AddModal = ({ handleCloseAddModal, onAdd, openEditModal, accountId, }) => {
    const [formData, setFormData] = useState({
        type: '',
        name: '',
        startTime: '',
        endTime: '',
        location: '51.505,-0.09',
        address: '',
        city: '',
        state: '',
        country: '',
        postalCode: '',
        note: '',

    });
    const [countryOptions, setCountryOptions] = useState([]);
    const [stateOptions, setStateOptions] = useState([]);
    const [cityOptions, setCityOptions] = useState([]);
    const [showMap, setShowMap] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [endDateMin, setEndDateMin] = useState(null);

    // Update end date minimum when start date changes
    useEffect(() => {
        if (formData.startTime) {
            setEndDateMin(formData.startTime);

            // If end date is before the new start date, clear it
            if (formData.endTime && formData.endTime < formData.startTime) {
                setFormData(prev => ({ ...prev, endTime: '' }));
            }
        }
    }, [formData.startTime]);

    // Load countries on component mount
    useEffect(() => {
        const countries = Country.getAllCountries().map((country) => ({
            value: country.name, // Store country name instead of isoCode
            label: country.name,
            isoCode: country.isoCode
        }));
        setCountryOptions([{ value: '', label: 'Select Country' }, ...countries]);
    }, []);

    // Update states when country changes
    useEffect(() => {
        if (formData.country) {
            const selectedCountry = countryOptions.find(c => c.value === formData.country);
            if (selectedCountry) {
                const states = State.getStatesOfCountry(selectedCountry.isoCode).map((state) => ({
                    value: state.name, // Store state name instead of isoCode
                    label: state.name,
                    isoCode: state.isoCode // Keep isoCode for city lookup
                }));
                setStateOptions([{ value: '', label: 'Select State' }, ...states]);
                setFormData((prev) => ({ ...prev, state: '', city: '' }));
                setCityOptions([{ value: '', label: 'Select City' }]);
            }
        } else {
            setStateOptions([{ value: '', label: 'Select State' }]);
            setCityOptions([{ value: '', label: 'Select City' }]);
        }
    }, [formData.country]);

    // Update cities when state changes
    useEffect(() => {
        if (formData.country && formData.state) {
            const selectedCountry = countryOptions.find(c => c.value === formData.country);
            const selectedState = stateOptions.find(s => s.value === formData.state);

            if (selectedCountry && selectedState) {
                const cities = City.getCitiesOfState(selectedCountry.isoCode, selectedState.isoCode).map((city) => ({
                    value: city.name,
                    label: city.name,
                }));
                setCityOptions([{ value: '', label: 'Select City' }, ...cities]);
                setFormData((prev) => ({ ...prev, city: '' }));
            }
        } else {
            setCityOptions([{ value: '', label: 'Select City' }]);
        }
    }, [formData.country, formData.state]);

    const typesOption = [
        { value: 'dispatch', label: 'Dispatch' },
        { value: 'project', label: 'Project' },
    ];

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // Handle select changes for dropdowns
    const handleSelectChange = (name, option) => {
        setFormData((prev) => ({
            ...prev,
            [name]: option ? option.value : '',
            // Reset dependent fields when country or state changes
            ...(name === 'country' && { state: '', city: '' }),
            ...(name === 'state' && { city: '' })
        }));
    };

    const handleLocationChange = (location) => {
        setFormData((prev) => ({ ...prev, location }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isLoading || successMessage) return;
        setIsLoading(true);
        setErrorMessage(null);
        setSuccessMessage(null);

        try {
            const utcStartTime = formData.startTime ? new Date(formData.startTime).toISOString() : '';
            const utcEndTime = formData.endTime ? new Date(formData.endTime).toISOString() : '';
            const payload = {
                type: formData.type,
                name: formData.name,
                location: formData.location,
                address: formData.address,
                city: formData.city,
                state: formData.state,
                country: formData.country,
                postalCode: formData.postalCode,
                startTime: utcStartTime,
                endTime: utcEndTime,
                note: formData.note
                // attributes: {
                //     priority: formData.priority,
                //     assignedTo: formData.assignedTo
                // },
            };

            const response = await createJob(payload, accountId);
            console.log("Created Job : ", response)
            if (response.success) {
                const newItem = response.data.data;
                onAdd?.(newItem);
                openEditModal?.(newItem);
                setSuccessMessage('Job added successfully!');
                setTimeout(() => {
                    handleCloseAddModal();
                }, 1000);
            } else {
                setErrorMessage(response.data.message || 'Failed to add Job');
            }
        } catch (error) {
            console.error('Error adding Job:', error);
            setErrorMessage('An error occurred while adding the Job');
        } finally {
            setIsLoading(false);
        }
    };

    const initialLocation = formData.location.split(',').map(Number);
    const mapPosition = { lat: initialLocation[0], lng: initialLocation[1] };

    return (
        <form onSubmit={handleSubmit} autoComplete='off' className="space-y-2 pt-3 relative">
            <ModalHeading className="text-left mb-3 border-b border-gray-300 px-4 pb-2">
                Add Job Detail
            </ModalHeading>

            <button
                className="hover:scale-105 absolute -top-2 right-2 bg-white border p-2 rounded-full"
                onClick={handleCloseAddModal}
            >
                <RxCross2 className="text-xl" />
            </button>

            {successMessage && (
                <div className="mb-4 px-4 py-2 bg-green-100 text-green-800 rounded-md text-sm text-center">
                    {successMessage}
                </div>
            )}

            {errorMessage && (
                <div className="mb-4 px-4 py-2 bg-red-100 text-red-800 rounded-md text-sm text-center">
                    {errorMessage}
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 px-4 gap-y-2 gap-x-2">
                <DateTimeLocalInput
                    label="Start Time"
                    name="startTime"
                    value={formData.startTime}
                    onChange={handleChange}
                    inputStyle="!py-[7px]"
                    labelGap="!gap-0.5"
                    labelStyle="text-gray-700"
                    required={true}
                    disabled={isLoading}
                />
                <DateTimeLocalInput
                    label="End Time"
                    name="endTime"
                    value={formData.endTime}
                    onChange={handleChange}
                    inputStyle="!py-[7px]"
                    labelGap="!gap-0.5"
                    labelStyle="text-gray-700"
                    required={true}
                    disabled={isLoading || !formData.startTime}
                    minDateTime={endDateMin}
                />

                <CustomReactSelect
                    label="Type"
                    name="type"
                    value={typesOption.find((option) => option.value === formData.type) || null}
                    onChange={(option) => handleSelectChange('type', option)}
                    options={typesOption}
                    required={true}
                    placeholder='Select type'
                    isDisabled={isLoading}
                />

                <CustomInput
                    label="Name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    inputStyle={'!py-[3px]'}
                    labelStyle={'text-gray-700'}
                    labelGap={'!gap-0.5'}
                    required={true}
                    disabled={isLoading}
                />
                <CustomInput
                    label="Address"
                    name="address"
                    type="text"
                    value={formData.address}
                    onChange={handleChange}
                    inputStyle={'!py-[3px]'}
                    labelStyle={'text-gray-700'}
                    labelGap={'!gap-0.5'}
                    required={true}
                    disabled={isLoading}
                />

                <CustomReactSelect
                    label="Country"
                    name="country"
                    value={countryOptions.find((option) => option.value === formData.country) || null}
                    onChange={(option) => handleSelectChange('country', option)}
                    options={countryOptions}
                    // required={true}
                    isDisabled={isLoading}
                />

                <CustomReactSelect
                    label="State"
                    name="state"
                    value={stateOptions.find((option) => option.value === formData.state) || null}
                    onChange={(option) => handleSelectChange('state', option)}
                    options={stateOptions}
                    // required={true}
                    isDisabled={!formData.country || isLoading}
                />

                <CustomReactSelect
                    label="City"
                    name="city"
                    value={cityOptions.find((option) => option.value === formData.city) || null}
                    onChange={(option) => handleSelectChange('city', option)}
                    options={cityOptions}
                    // required={true}
                    isDisabled={!formData.state || isLoading}
                />


                <CustomInput
                    label="Postal Code"
                    name="postalCode"
                    type="text"
                    value={formData.postalCode}
                    onChange={handleChange}
                    inputStyle={'!py-[3px]'}
                    labelStyle={'text-gray-700'}
                    labelGap={'!gap-0.5'}
                    required={true}
                    disabled={isLoading}
                />

                <div className={`flex flex-col sm:col-span-2 ${showMap ? 'mb-20' : 'mb-0'} gap-1.5 w-full`}>
                    <button
                        type="button"
                        className={` bg-gray-200 px-2 text-sm py-1.5 rounded-lg mt-2`}
                        onClick={() => setShowMap(!showMap)}
                    >
                        {showMap ? 'Hide Map' : 'Select Location on Map'}
                    </button>
                    <GoogleMapComponent
                        isVisible={showMap}
                        initialLocation={mapPosition}
                        onLocationChange={handleLocationChange}
                    />
                </div>

                <div className="w-full sm:col-span-2">
                    <CustomTextarea
                        label="Note"
                        name="note"
                        value={formData.note}
                        onChange={handleChange}
                        placeholder=""
                        labelGap="!gap-0.5"
                        labelStyle="text-gray-700"
                        rows={2}
                        maxLength={500}
                    />
                </div>
            </div>

            <div className="flex justify-end px-4">
                <Button
                    label="Save"
                    type="submit"
                    loading={isLoading}
                    loadingLabel="Saving..."
                    className={`bg-btnBg rounded-lg !py-1 text-white ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={isLoading || !!successMessage}
                />
            </div>
        </form>
    );
};

export default AddModal;